import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { APP_CONFIG } from '../constants/APP_CONFIG';
import { addCorrelationIdToRequests } from './interceptors/addCorrelationIdToRequests';
import { addCsrfToRequests } from './interceptors/addCsrfToRequests';
import { convertFormDataOnLocalDev } from './interceptors/convertFormDataOnLocalDev';
import { stripManheimDomainsFromRequestUrl } from './interceptors/stripManheimDomainsFromRequestUrl';
import { createHttpClient } from './utils/createHttpClient';

const httpClient = createHttpClient({
  config: {
    baseURL: APP_CONFIG.services.sdGateway.baseUrl,
    withCredentials: true,
    xsrfCookieName: 'THIS-IS-NOT-USED'
  },
  interceptors: [
    convertFormDataOnLocalDev,
    addCorrelationIdToRequests,
    addCsrfToRequests,
    stripManheimDomainsFromRequestUrl
  ]
});

const LS_CACHE_KEY = 'dashboard-gateway-client-cache';
const cacheDuration = 5 * 60 * 1000;
const cachedAxiosResponse = {
  status: 200,
  statusText: 'From Local Cache',
  headers: []
};

type LSCacheResponse<T> = {
  valid: boolean;
  value?: T;
};

type cacheObject = {
  [key: string]: any;
};

function getPurgedCache() {
  const now = Date.now();
  const cache = JSON.parse(localStorage.getItem(LS_CACHE_KEY) ?? '{}');

  const purgedCache = Object.keys(cache).reduce<cacheObject>((acc, index) => {
    const cacheItemDate = parseInt(cache[index].date ?? '0') + cacheDuration;
    if (cacheItemDate > now) {
      return { ...acc, ...{ [index]: cache[index] } };
    }
    return acc;
  }, {});
  return purgedCache;
}

function fetchFromCache<T>(key: string): LSCacheResponse<T> {
  try {
    const cache = getPurgedCache();

    if (Object.keys(cache).includes(key)) {
      return { valid: true, value: cache[key].value };
    }
    return { valid: false };
  } catch {
    return { valid: false };
  }
}

const putInCache = (key: string, value: any) => {
  const cache = getPurgedCache();
  const cacheEntry = { [key]: { date: Date.now(), value } };

  localStorage.setItem(LS_CACHE_KEY, JSON.stringify({ ...cache, ...cacheEntry }));
};

const cacheGetAction: CachedGetInstance = {
  get<T = any>(url: string, config?: AxiosRequestConfig, cacheKey?: string): Promise<AxiosResponse<T>> {
    if (cacheKey) {
      const cacheData = fetchFromCache<T>(cacheKey);
      if (cacheData.valid && cacheData.value !== undefined) {
        const response: AxiosResponse<T> = { data: cacheData.value, config: config ?? {}, ...cachedAxiosResponse };
        return Promise.resolve(response);
      }
    }

    const response = httpClient.get<T>(url, config);

    return response.then((value: AxiosResponse<T>) => {
      if (cacheKey && value.data) {
        putInCache(cacheKey, value.data);
      }
      return value;
    });
  }
};

type CachedGetInstance = {
  get<T = any>(url: string, config?: AxiosRequestConfig, cacehKey?: string): Promise<AxiosResponse<T>>;
};
type AxiosCached = CachedGetInstance & AxiosInstance;

/**
 * Makes requests via the seller-dashboard-gateway proxy
 * Entities can be expanded via the X-Seller-Dashboard-Gateway-Expand header
 * @see
 * Full whitelist of URLs: https://ghe.coxautoinc.com/SellerTools/seller-dashboard-gateway/blob/master/src/handlers/whitelistProxy/whitelist.ts
 * @example
 * client.get('/units/id/FOO', {headers: {'X-Seller-Dashboard-Gateway-Expand': 'media,sellerDescriptions'}});
 */
export const sdGatewayClient: AxiosCached = { ...httpClient, ...cacheGetAction } as AxiosCached;
